import { httpsCallable, FunctionsError } from 'firebase/functions';
import type { TenantFeature } from '@kawai/shared';
import { functions } from '../firebase.js';

export interface IssueImpersonationTokenInput {
  targetTenantId: string;
  /** 1 〜 60 分。未指定なら functions 側のデフォルト (30 分) */
  durationMinutes?: number;
}

export interface IssueImpersonationTokenResponse {
  ok: true;
  customToken: string;
  uid: string;
  targetTenantId: string;
  /** JST ISO8601、表示・URL 期限切れ判定用 */
  expiresAt: string;
  impersonatedBy: string;
  features: TenantFeature[];
}

const callable = httpsCallable<
  IssueImpersonationTokenInput,
  IssueImpersonationTokenResponse
>(functions, 'tenantIssueImpersonationToken');

/**
 * S-07: `tenantIssueImpersonationToken` Callable の薄いラッパ。
 * Functions 側で saas-admin role 検証 / tenants 存在チェック / Custom Token 発行を行い、
 * 失敗時は FunctionsError を throw する。
 */
export async function callIssueImpersonationToken(
  input: IssueImpersonationTokenInput,
): Promise<IssueImpersonationTokenResponse> {
  const res = await callable(input);
  return res.data;
}

/**
 * `tenantIssueImpersonationToken` のエラーを saas-admin 向け日本語メッセージに変換。
 *
 * - not-found: targetTenantId が存在しない
 * - failed-precondition: tenant が active 以外（suspended / archived）
 * - invalid-argument: 入力値検証 NG
 * - permission-denied: saas-admin 以外
 * - unauthenticated: セッション切れ
 */
export function impersonationErrorMessage(err: unknown): string {
  if (err instanceof FunctionsError) {
    switch (err.code) {
      case 'functions/not-found':
        return '指定したテナントが見つかりません。';
      case 'functions/failed-precondition':
        return 'このテナントは active ではありません（impersonation 不可）。';
      case 'functions/invalid-argument':
        return `入力値に誤りがあります：${err.message}`;
      case 'functions/permission-denied':
        return '権限がありません（saas-admin 権限が必要です）。';
      case 'functions/unauthenticated':
        return 'セッションが切れました。再ログインしてください。';
      default:
        return `impersonation トークンの発行に失敗しました（${err.code}）。`;
    }
  }
  if (err instanceof Error) {
    return `impersonation トークンの発行に失敗しました：${err.message}`;
  }
  return 'impersonation トークンの発行に失敗しました。';
}
